/**
 * API Key Settings Page - Configuration for AI Service API Keys
 * Master Role Only
 */
import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  TextField,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Alert,
  Snackbar,
  CircularProgress,
  Card,
  CardContent,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  InputAdornment,
  Tooltip,
  Fade,
  Grid,
} from '@mui/material';
import {
  VpnKey as KeyIcon,
  Save as SaveIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  CheckCircle as CheckIcon,
  Cancel as CancelIcon,
  ArrowBack as BackIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import config from '../config';
import { getApiKeyErrorMessage } from '../utils/errorHandler';

const API_BASE_URL = config.apiUrl;

export default function APIKeySettingsPage() {
  const navigate = useNavigate();
  const { t } = useTranslation(['common', 'settings']);
  const tSettings = (key, options) => t(key, { ns: 'settings', ...options });
  const tApiKeys = (key, options) => t(`apiKeys.${key}`, { ns: 'settings', ...options });

  // Form state
  const [provider, setProvider] = useState('google');
  const [modelName, setModelName] = useState('gemini-2.5-pro');
  const [apiKey, setApiKey] = useState('');
  const [showApiKey, setShowApiKey] = useState(false);

  // UI state
  const [loading, setLoading] = useState(false);
  const [loadingList, setLoadingList] = useState(true);
  const [apiKeysList, setApiKeysList] = useState([]);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [deleteDialog, setDeleteDialog] = useState({ open: false, id: null });
  const [editMode, setEditMode] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [currentKeyPreview, setCurrentKeyPreview] = useState('');

  // Provider options
  const providers = [
    { value: 'google', label: 'Google Gemini', icon: '🔷' },
    { value: 'openai', label: 'OpenAI', icon: '🤖', disabled: true },
    { value: 'anthropic', label: 'Anthropic Claude', icon: '🧠', disabled: true },
  ];

  // Model options based on provider
  const modelOptions = {
    google: [
      { value: 'gemini-3-pro-preview', label: 'Gemini 3 Pro Preview (Latest)' },
      { value: 'gemini-2.5-pro', label: 'Gemini 2.5 Pro' },
      { value: 'gemini-flash-latest', label: 'Gemini 2.5 Flash Latest' },
    ],
    openai: [
      { value: 'gpt-4-turbo', label: 'GPT-4 Turbo' },
      { value: 'gpt-3.5-turbo', label: 'GPT-3.5 Turbo' },
    ],
    anthropic: [
      { value: 'claude-3-opus', label: 'Claude 3 Opus' },
      { value: 'claude-3-sonnet', label: 'Claude 3 Sonnet' },
    ],
  };

  // Load API keys list
  useEffect(() => {
    loadAPIKeys();
  }, []);

  const loadAPIKeys = async () => {
    setLoadingList(true);
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.get(`${API_BASE_URL}/api/v1/settings/api-keys`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setApiKeysList(response.data.settings || []);
    } catch (error) {
      console.error('Error loading API keys:', error);
      showSnackbar(tApiKeys('errorLoading'), 'error');
    } finally {
      setLoadingList(false);
    }
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate: require key for new entries
    if (!editMode && !apiKey.trim()) {
      showSnackbar(tApiKeys('errorEmptyKey'), 'error');
      return;
    }

    setLoading(true);

    try {
      const token = localStorage.getItem('access_token');
      const payload = {
        provider,
        model_name: modelName,
        is_active: true,
        metadata: {},
      };

      // Only include api_key if it's provided
      if (apiKey.trim()) {
        payload.api_key = apiKey;
      }

      if (editMode && editingId) {
        // Update existing
        await axios.put(
          `${API_BASE_URL}/api/v1/settings/api-keys/${editingId}`,
          payload,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        showSnackbar(tApiKeys('successUpdate'), 'success');
      } else {
        // Create new
        await axios.post(`${API_BASE_URL}/api/v1/settings/api-keys`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
        showSnackbar(tApiKeys('successCreate'), 'success');
      }

      // Reset form
      resetForm();
      loadAPIKeys();
    } catch (error) {
      console.error('Error saving API key:', error);
      const errorMsg = getApiKeyErrorMessage(error, t);
      showSnackbar(errorMsg, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (item) => {
    setProvider(item.provider);
    setModelName(item.model_name);
    setApiKey('');
    setCurrentKeyPreview(item.api_key_preview || '');
    setEditMode(true);
    setEditingId(item.id);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDelete = async () => {
    const { id } = deleteDialog;

    try {
      const token = localStorage.getItem('access_token');
      await axios.delete(`${API_BASE_URL}/api/v1/settings/api-keys/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      showSnackbar(tApiKeys('successDelete'), 'success');
      loadAPIKeys();
    } catch (error) {
      console.error('Error deleting API key:', error);
      showSnackbar(tApiKeys('errorDeleting'), 'error');
    } finally {
      setDeleteDialog({ open: false, id: null });
    }
  };

  const resetForm = () => {
    setProvider('google');
    setModelName('gemini-2.5-pro-preview-05-06');
    setApiKey('');
    setCurrentKeyPreview('');
    setEditMode(false);
    setEditingId(null);
  };

  const handleCancelEdit = () => {
    resetForm();
  };

  return (
    <Box
      sx={{
        height: '100%',
        overflow: 'auto',
        bgcolor: '#f5f7fa',
      }}
    >
      {/* Header - Modern Design */}
      <Paper
        elevation={0}
        sx={{
          mb: 4,
          p: 3,
          borderRadius: 3,
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {/* Background decoration */}
        <Box
          sx={{
            position: 'absolute',
            top: -50,
            right: -50,
            width: 200,
            height: 200,
            borderRadius: '50%',
            background: 'rgba(255,255,255,0.1)',
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            bottom: -30,
            right: 100,
            width: 100,
            height: 100,
            borderRadius: '50%',
            background: 'rgba(255,255,255,0.05)',
          }}
        />
        
        <Box sx={{ display: 'flex', alignItems: 'center', position: 'relative', zIndex: 1 }}>
          <IconButton 
            onClick={() => navigate('/settings')} 
            sx={{ 
              mr: 2, 
              color: 'white',
              bgcolor: 'rgba(255,255,255,0.1)',
              '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' }
            }}
          >
            <BackIcon />
          </IconButton>
          <Box
            sx={{
              width: 56,
              height: 56,
              borderRadius: 2,
              bgcolor: 'rgba(255,255,255,0.2)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mr: 2,
            }}
          >
            <KeyIcon sx={{ fontSize: 32 }} />
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h4" fontWeight="600">{tApiKeys('title')}</Typography>
            <Typography variant="body2" sx={{ opacity: 0.9, mt: 0.5 }}>
              {tApiKeys('subtitle')} • {apiKeysList.length} {tApiKeys('keysConfigured')}
            </Typography>
          </Box>
        </Box>
      </Paper>

      <Container maxWidth="lg" sx={{ py: 0 }}>

        <Grid container spacing={3}>
          {/* Configuration Form */}
          <Grid item xs={12} md={6}>
            <Fade in timeout={400}>
              <Paper
                elevation={0}
                sx={{
                  p: 4,
                  borderRadius: 3,
                  border: '1px solid #e0e0e0',
                }}
              >
                <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
                  {editMode ? tApiKeys('editConfig') : tApiKeys('newConfig')}
                </Typography>

                <form onSubmit={handleSubmit}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                    {/* Provider Selection */}
                    <FormControl fullWidth>
                      <InputLabel>{tApiKeys('provider')}</InputLabel>
                      <Select
                        value={provider}
                        label={tApiKeys('provider')}
                        onChange={(e) => {
                          setProvider(e.target.value);
                          setModelName(modelOptions[e.target.value][0].value);
                        }}
                      >
                        {providers.map((p) => (
                          <MenuItem key={p.value} value={p.value} disabled={p.disabled}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <span>{p.icon}</span>
                              <span>{p.label}</span>
                              {p.disabled && (
                                <Chip
                                  label={tSettings('comingSoon')}
                                  size="small"
                                  sx={{ ml: 1, height: 20, fontSize: '0.7rem' }}
                                />
                              )}
                            </Box>
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>

                    {/* Model Selection */}
                    <FormControl fullWidth>
                      <InputLabel>{tApiKeys('model')}</InputLabel>
                      <Select
                        value={modelName}
                        label={tApiKeys('model')}
                        onChange={(e) => setModelName(e.target.value)}
                      >
                        {modelOptions[provider].map((model) => (
                          <MenuItem key={model.value} value={model.value}>
                            {model.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>

                    {/* Current API Key Preview (Edit Mode Only) */}
                    {editMode && currentKeyPreview && (
                      <TextField
                        fullWidth
                        label={tApiKeys('currentKey')}
                        value={currentKeyPreview}
                        InputProps={{
                          readOnly: true,
                        }}
                        sx={{
                          '& .MuiInputBase-input': {
                            fontFamily: 'monospace',
                            bgcolor: '#f5f5f5',
                          },
                        }}
                        helperText={tApiKeys('currentKeyHelp')}
                      />
                    )}

                    {/* API Key Input */}
                    <TextField
                      fullWidth
                      label={editMode ? tApiKeys('newApiKey') : tApiKeys('apiKey')}
                      type={showApiKey ? 'text' : 'password'}
                      value={apiKey}
                      onChange={(e) => setApiKey(e.target.value)}
                      required={!editMode}
                      placeholder={editMode ? tApiKeys('leaveEmptyToKeep') : ''}
                      sx={{
                        '& .MuiInputBase-input': {
                          fontFamily: 'monospace',
                          fontSize: '0.95rem',
                        },
                      }}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={() => setShowApiKey(!showApiKey)}
                              edge="end"
                            >
                              {showApiKey ? <VisibilityOffIcon /> : <VisibilityIcon />}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />

                    {/* Action Buttons */}
                    <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
                      <Button
                        type="submit"
                        variant="contained"
                        fullWidth
                        startIcon={loading ? <CircularProgress size={20} /> : <SaveIcon />}
                        disabled={loading}
                        sx={{
                          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                          py: 1.5,
                          fontWeight: 600,
                          textTransform: 'none',
                          fontSize: '1rem',
                        }}
                      >
                        {editMode ? t('common.update') : t('common.save')}
                      </Button>

                      {editMode && (
                        <Button
                          variant="outlined"
                          onClick={handleCancelEdit}
                          startIcon={<CancelIcon />}
                          sx={{ py: 1.5 }}
                        >
                          {t('common.cancel')}
                        </Button>
                      )}
                    </Box>
                  </Box>
                </form>
              </Paper>
            </Fade>
          </Grid>

          {/* API Keys List */}
          <Grid item xs={12} md={6}>
            <Fade in timeout={500}>
              <Paper
                elevation={0}
                sx={{
                  p: 4,
                  borderRadius: 3,
                  border: '1px solid #e0e0e0',
                  maxHeight: 700,
                  overflow: 'auto',
                }}
              >
                <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
                  {tApiKeys('configuredKeys')}
                </Typography>

                {loadingList ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <CircularProgress />
                  </Box>
                ) : apiKeysList.length === 0 ? (
                  <Box sx={{ textAlign: 'center', py: 4 }}>
                    <KeyIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
                    <Typography variant="body2" color="text.secondary">
                      {tApiKeys('noKeys')}
                    </Typography>
                  </Box>
                ) : (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {apiKeysList.map((item) => (
                      <Card
                        key={item.id}
                        sx={{
                          border: item.is_active ? '2px solid #667eea' : '1px solid #e0e0e0',
                          borderRadius: 2,
                          transition: 'all 0.2s',
                          '&:hover': {
                            boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                          },
                        }}
                      >
                        <CardContent>
                          <Box
                            sx={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'flex-start',
                            }}
                          >
                            <Box sx={{ flexGrow: 1 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                  {providers.find((p) => p.value === item.provider)?.icon}{' '}
                                  {item.provider.toUpperCase()}
                                </Typography>
                                {item.is_active && (
                                  <Chip
                                    icon={<CheckIcon sx={{ fontSize: 14 }} />}
                                    label={tApiKeys('active')}
                                    size="small"
                                    color="success"
                                    sx={{ height: 22 }}
                                  />
                                )}
                              </Box>
                              <Typography variant="body2" color="text.secondary" gutterBottom>
                                {tApiKeys('model')}: {item.model_name}
                              </Typography>
                              <Typography
                                variant="caption"
                                sx={{
                                  fontFamily: 'monospace',
                                  bgcolor: '#f5f5f5',
                                  px: 1,
                                  py: 0.5,
                                  borderRadius: 1,
                                  display: 'inline-block',
                                }}
                              >
                                {item.api_key_preview}
                              </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', gap: 1 }}>
                              <Tooltip title={t('common.edit')}>
                                <IconButton
                                  size="small"
                                  onClick={() => handleEdit(item)}
                                  sx={{ color: '#667eea' }}
                                >
                                  <EditIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title={t('common.delete')}>
                                <IconButton
                                  size="small"
                                  onClick={() => setDeleteDialog({ open: true, id: item.id })}
                                  sx={{ color: '#f44336' }}
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          </Box>
                        </CardContent>
                      </Card>
                    ))}
                  </Box>
                )}
              </Paper>
            </Fade>
          </Grid>
        </Grid>

        {/* Info Alert */}
        <Fade in timeout={600}>
          <Alert severity="info" sx={{ mt: 3, borderRadius: 2 }}>
            <Typography variant="body2">
              {tApiKeys('infoMessage')}
            </Typography>
          </Alert>
        </Fade>
      </Container>

      {/* Delete Confirmation Dialog */}
      <Dialog 
        open={deleteDialog.open} 
        onClose={() => setDeleteDialog({ open: false, id: null })}
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ fontWeight: 600 }}>
          ⚠️ {tApiKeys('confirmDelete')}
        </DialogTitle>
        <DialogContent>
          <Typography color="text.secondary">{tApiKeys('confirmDeleteMessage')}</Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDeleteDialog({ open: false, id: null })} sx={{ borderRadius: 2 }}>
            {t('common.cancel')}
          </Button>
          <Button 
            onClick={handleDelete} 
            color="error" 
            variant="contained"
            sx={{ borderRadius: 2 }}
          >
            {t('common.delete')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}


